import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DialogData, EbwModalService } from '@ebw-monorepo/modal';
import { TranslateService } from '@ngx-translate/core';
import { Feature } from 'ol';
import { CustomerCategoryEnum } from '../../../../../utils/src/lib/enums/customer-category.enum';
import { CustomerEnum } from '../../../../../utils/src/lib/enums/customer.enum';
import { FlatNode } from '../../models/ebw-tree.model';
import { ConfigSidebarService } from '../../services/config-sidebar.service';
import { FeatureSelectService } from '../../services/feature-select.service';
import { TelcoService } from '../../services/telco.service';
import { parseUrn } from '../../utils/urn.utils';

export interface ReportDialogStyleConfig {
  container?: string;
  wrapperItem?: string;
  wrapperSubItem?: string;
  wrapperData?: string;
  keyValue?: string;
  key?: string;
  value?: string;
}

export const REPORT_DIALOG_CUSTOM_STYLE: ReportDialogStyleConfig = {
  container: 'custom-report-dialog-container',
  wrapperItem: 'custom-wrapper-item',
  wrapperSubItem: undefined,
  wrapperData: 'custom-wrapper-data',
  keyValue: 'custom-item-key-value',
  key: 'custom-item-key',
  value: 'custom-item-value'
};

export interface ReportDialogData {
  data?: { key: string; value: any }[];
  showStructure?: boolean;
  properties?: any;
  styleConfig?: ReportDialogStyleConfig;
  changeComponent?: string;
}

@Component({
  selector: 'app-report-dialog',
  templateUrl: './report-dialog.component.html',
  styleUrls: ['./report-dialog.component.scss']
})
export class ReportDialogComponent implements OnInit, OnDestroy {
  @Input() public isDialog = true;
  @Input() styleConfig?: ReportDialogStyleConfig;
  @Output() public detailRequested: EventEmitter<Feature> = new EventEmitter();

  customerEnum = CustomerEnum;
  customerCategoryEnum = CustomerCategoryEnum;

  private _changeComponent: string;
  private _data: { key: string; value: string; internalKey: string }[];
  private _joinProperties: { key: string; value: any }[] = [];
  private _showStructure: boolean = true;
  private _properties: any;

  urn = '';

  get showStructure() {
    return this._showStructure;
  }

  @Input() set showStructure(value: boolean) {
    this._showStructure = value;
  }

  get data(): any {
    return this._data;
  }

  @Input() set data(data: { key: string; value: string }[]) {
    if (!data) {
      return;
    }

    const foundField: { key: string; value: string } = data.find((el: { key: string; value: unknown }) => el.key === 'urn');

    if (foundField) {
      this.urn = foundField.value;
    }

    const { properties, joinProperties } = this._featureSelectService.parseDataForReportDialogUI(data);
    this._joinProperties = joinProperties;
    this._data = properties;
  }

  get joinProperties(): any {
    return this._joinProperties;
  }

  get changeComponent(): string {
    return this._changeComponent;
  }

  @Input() set changeComponent(changeComponent: string) {
    this._changeComponent = changeComponent;
  }

  @Input() set reportData(d: ReportDialogData) {
    if (!d) {
      return;
    }
    if (d.data !== undefined) {
      this.data = d.data;
    }
    if (d.showStructure !== undefined) {
      this.showStructure = d.showStructure;
    }
    if (d.properties !== undefined) {
      this.properties = d.properties;
    }
    if (d.styleConfig !== undefined) {
      this.styleConfig = d.styleConfig;
    }
    if (d.changeComponent !== undefined) {
      this.changeComponent = d.changeComponent;
    }
  }

  get properties(): any {
    return this._properties;
  }

  @Input() set properties(properties: any) {
    this._properties = properties;
  }

  // --------------------------------------------------------------------------------------------------------------------

  constructor(
    private _router: Router,
    @Optional() public dialogRef: MatDialogRef<ReportDialogComponent>,
    @Optional() private _globalDialogRef: MatDialog,
    @Optional() @Inject(MAT_DIALOG_DATA) public matData: DialogData,
    private _featureSelectService: FeatureSelectService,
    private _telcoService: TelcoService,
    private _ebwModalService: EbwModalService,
    private _translateService: TranslateService,
    private _configSidebarService: ConfigSidebarService
  ) {}

  // --------------------------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    if (this.matData && this.isDialog) {
      this.reportData = this.matData as unknown as ReportDialogData;
    }
  }

  // --------------------------------------------------------------------------------------------------------------------

  ngOnDestroy(): void {
    this._telcoService.clearTracciatoIRUFromMap();
  }

  // --------------------------------------------------------------------------------------------------------------------

  isFwLink = (urn: string) => urn.includes('fw_link');

  // --------------------------------------------------------------------------------------------------------------------

  isFwCircuit = (urn: string) => urn.includes('fw_circuit');

  // --------------------------------------------------------------------------------------------------------------------

  isFwContract = (urn: string) => urn.includes('fw_contract');

  // --------------------------------------------------------------------------------------------------------------------

  // match esatto sulla collection: 'sheath_with_loc' è un'altra collection
  isSheath = (urn: string) => parseUrn(urn).collection === 'sheath';

  // --------------------------------------------------------------------------------------------------------------------

  isFwSpliceClosure = (urn: string) => urn.includes('splice_closure');
  // --------------------------------------------------------------------------------------------------------------------
  isFwBuilding = (urn: string) => urn.includes('building');
  // --------------------------------------------------------------------------------------------------------------------
  hasConnectivity = (urn: string) => this.isFwSpliceClosure(urn) || this.isFwBuilding(urn);
  // --------------------------------------------------------------------------------------------------------------------

  isArray(value: unknown) {
    return value instanceof Array;
  }

  isObject(value: unknown) {
    return typeof value === 'object' && !Array.isArray(value) && value !== null;
  }

  // --------------------------------------------------------------------------------------------------------------------

  findPlantKey() {
    if (this._configSidebarService.getExternalSettings() === undefined || this._configSidebarService.getExternalSettings() === null) {
      return undefined;
    }

    const keyToFind = this._configSidebarService.getExternalSettings().key;

    const found = Object.keys(this._properties).find((key: any) => {
      return key === keyToFind && this._properties[key];
    });

    if (found) {
      return this._properties[found];
    }

    return undefined;
  }

  // --------------------------------------------------------------------------------------------------------------------

  goToOtdr({ urn, name }) {
    this._router
      .navigate(['/home', { outlets: { sidebarAction: ['otdr'] } }], {
        state: { urn, name }
      })
      .then(() => {
        if (this.isDialog) {
          this.dialogRef.close();
          this._globalDialogRef.closeAll();
        }
        this._featureSelectService.openOtdrPanel$.next(true);
      });
  }

  // --------------------------------------------------------------------------------------------------------------------

  async goToDetail(node) {
    const feature = await this._featureSelectService.fetchFeatureByUrn(node.urn);
    if (!feature) {
      return;
    }

    if (this.isDialog) {
      this._featureSelectService.mapFeatureToModal(feature, 'view');
    } else {
      this.detailRequested.emit(feature);
    }
  }

  // --------------------------------------------------------------------------------------------------------------------

  goToEPlant() {
    const eplantKey: string = this.findPlantKey();

    window.open(this._configSidebarService.getExternalSettings().portal + eplantKey, '_blank');
  }

  // --------------------------------------------------------------------------------------------------------------------

  exportPercorso = (type) => this._telcoService.downloadExportReportPercorso(this.urn, type);

  // --------------------------------------------------------------------------------------------------------------------

  exportLunghezzeCavo = (type) => this._telcoService.downloadExportLunghezzeCavo(this.urn, type);

  // --------------------------------------------------------------------------------------------------------------------

  getContract() {
    const id = parseUrn(this.urn).id;

    this._telcoService.getFwContractsDocumentUrl(id).subscribe({
      next: (res) => {
        if (res && res.error) {
          const errorTitle = this._translateService.instant('common.error');
          if (res.error === 404) {
            this._ebwModalService.openErrorModal({ title: errorTitle, content: 'Contratto non trovato' });
            return;
          }

          if (res.message) {
            this._ebwModalService.openErrorModal({ title: errorTitle, content: res.message });
            return;
          }

          this._ebwModalService.openErrorModal({ title: errorTitle, content: this._translateService.instant('common.no_result') });
        }

        this._ebwModalService
          .openConfirmModal({
            title: this._translateService.instant('map.sidebar.contract.open_contract_modal_title'),
            content: this._translateService.instant('map.sidebar.contract.open_contract_modal_desc')
          })
          .afterClosed()
          .subscribe((result) => {
            if (result == true) {
              window.open(res.result, '_blank');
            }
          });
      },
      error: (error) => {
        const errorTitle = this._translateService.instant('common.error');
        this._ebwModalService.openErrorModal({ title: errorTitle, content: error });
      }
    });
  }

  goToRaccordi(node: FlatNode) {
    this._router.navigate(['/home', { outlets: { sidebarAction: ['fiber-joints'] } }], { queryParams: { urn: node.urn } }).then(() => {
      if (this.isDialog) {
        this.dialogRef.close();
        this._globalDialogRef.closeAll();
      }
    });
  }
}
